import EmojiOne from 'emojione';
import isHtml from 'is-html';
import juiceClient from 'juice/client';
import { config } from 'lib-shared-backend-config';
import { Message, MetaConversation } from 'lib-shared-legacy-types';
import { User } from 'lib-shared-types-api';

import {
  convertPlainToHtml,
  getDirectionFormattedHtml,
} from 'app/utils/htmlFormatting';
import {
  addTargetAndRelToLinks,
  getMessageSanitizationChannel,
  SanitizeHTML,
} from 'app/utils/htmlSanitizer';
import markdownToHTML from 'app/utils/markdownToHTML';

import { EmailMessageType } from '../../components/conversation/reducers/emailMessage';

import {
  AttachmentFile,
  prepareAttachments,
  prepareInlineImages,
} from './attachments';
import { getTranscriptDateLead } from './conversationTitle';

const LINEBREAK = `<br /><br />`;

// if using html email, then find the last message with a rewritten html message
// and return that as well as the index it's at
// else return [-1, null]
function findLastHTMLEmail(
  messagesToBeCopied: Message[],
  rewrittenHtmlEmailMessages: EmailMessageType[],
  hasFFWriteHTMLEmail: boolean,
): [number, EmailMessageType | null, Message | null] {
  if (hasFFWriteHTMLEmail) {
    const lastHTMLEmailIndex =
      messagesToBeCopied.length -
      1 -
      messagesToBeCopied
        .slice()
        .reverse()
        .findIndex(
          (message: Message) =>
            message.email_original_content_url &&
            // It has to be an inbound email as the rewritten html messages will
            // otherwise not contain the email.
            message.direction === 'inbound',
        );
    const lastMessage = messagesToBeCopied[lastHTMLEmailIndex];
    const foundRewrittenHtmlEmailMessage: any | null =
      lastMessage &&
      lastMessage.email_original_content_url &&
      rewrittenHtmlEmailMessages.find((htmlMsg: EmailMessageType) =>
        (lastMessage.email_original_content_url as string).includes(
          htmlMsg.messageId,
        ),
      );

    if (foundRewrittenHtmlEmailMessage) {
      return [lastHTMLEmailIndex, foundRewrittenHtmlEmailMessage, lastMessage];
    }
  }

  return [-1, null, null];
}

function getTranscriptMessage(
  message: Message,
  messageContent: string | null | undefined,
  hasFFWriteHTMLEmail: boolean,
): string {
  const date = new Date(message.created_at as string);
  const dateLead = getTranscriptDateLead(
    date,
    hasFFWriteHTMLEmail ? 'html' : 'markdown',
  );
  return `${
    message.author ? message.author.name || message.author.email || '' : ''
  } ${dateLead}${LINEBREAK}${messageContent}${LINEBREAK}`;
}

type MessagesTranscript = {
  transcript: string;
  authors: User[];
  attachments: AttachmentFile[];
};

export function getMessagesTranscript(
  metaConversation: MetaConversation | null | undefined,
  rewrittenHtmlEmailMessages: EmailMessageType[],
  hasFFWriteHTMLEmail: boolean,
): MessagesTranscript {
  const messagesToBeCopied: Message[] =
    metaConversation?.messages.filter((message: Message) =>
      ['conversation:add_message', 'conversation:message_added'].includes(
        message.type,
      ),
    ) ?? [];

  const [
    lastHTMLEmailIndex,
    foundRewrittenHtmlEmailMessage,
    lastInboundMessage,
  ] = findLastHTMLEmail(
    messagesToBeCopied,
    rewrittenHtmlEmailMessages,
    hasFFWriteHTMLEmail,
  );

  // check feature flag, and if a html email was actually found
  const hasHTMLEmail = !!(
    hasFFWriteHTMLEmail &&
    lastHTMLEmailIndex > -1 &&
    foundRewrittenHtmlEmailMessage
  );

  return messagesToBeCopied.reduce<MessagesTranscript>(
    (acc, message, index) => {
      // We only want to add the author to the contact list
      // if the author has actually added a message to the conversation
      const foundAuthorIndex = acc.authors.findIndex(
        (a: User) => a && a.id === message.author.id,
      );
      const authors =
        foundAuthorIndex === -1
          ? [...acc.authors, message.author]
          : acc.authors;

      // Add attachments
      const attachments: AttachmentFile[] = [
        ...acc.attachments,
        ...prepareAttachments(message.attached_files),
        ...prepareInlineImages(message.inline_images),
      ];

      let transcript: string = acc.transcript;
      if (
        index === lastHTMLEmailIndex &&
        lastInboundMessage &&
        foundRewrittenHtmlEmailMessage &&
        typeof foundRewrittenHtmlEmailMessage.rewritten_html === 'string' &&
        foundRewrittenHtmlEmailMessage.rewritten_html.trim().length > 0 &&
        ['contactform', 'email'].includes(
          metaConversation?.conversation.initial_channel ?? '',
        )
      ) {
        // write the html transcript if this is the last html message
        transcript += `${getTranscriptMessage(
          lastInboundMessage,
          juiceClient(foundRewrittenHtmlEmailMessage.rewritten_html),
          hasHTMLEmail,
        )}`;
      } else if (hasHTMLEmail && index > lastHTMLEmailIndex) {
        // prepend markdown messages if this message is after the last html one
        // this is to preserve the order of latest message at the top
        transcript = `${getTranscriptMessage(
          message,
          getMessageMarkup(message),
          hasHTMLEmail,
        )}${transcript}`;
      } else if (!hasHTMLEmail) {
        // otherwise append markdown messages with order of latest message at the bottom
        transcript = `${transcript}${getTranscriptMessage(
          message,
          getMessageMarkup(message),
          hasHTMLEmail,
        )}`;
      }

      return {
        transcript,
        attachments,
        authors,
      };
    },
    {
      transcript: '',
      attachments: [],
      authors: [],
    },
  );
}

export function prepareOriginalEmailUrl(url: string): string {
  // Adds https://files.dixa.io (or staging) as prefix if missing.
  if (/^https?:\/\//i.test(url) === false) {
    return config.url.userContent.replace(/\/$/, '') + url;
  }
  return url.replace(`${config.url.files}/`, config.url.userContent);
}

/** Returns proper markup based on the message context. */
export function getMessageMarkup(message: Message): string {
  const sanitizationChannel = getMessageSanitizationChannel(message);
  let preparedMarkup = SanitizeHTML.byChannel(
    message.text ?? '',
    sanitizationChannel,
  );

  if (message && (message.channel || message.type)) {
    if (message.channel === 'contactform') {
      if (message.direction === 'inbound' && !isHtml(preparedMarkup)) {
        preparedMarkup = getDirectionFormattedHtml(
          convertPlainToHtml(EmojiOne.shortnameToUnicode(preparedMarkup)),
        );
      } else {
        preparedMarkup = markdownToHTML(
          EmojiOne.shortnameToUnicode(preparedMarkup),
        );
      }
    } else if (message.channel === 'messenger') {
      const emojified = EmojiOne.shortnameToUnicode(preparedMarkup);
      preparedMarkup =
        message.content_type === 'Html' ? emojified : markdownToHTML(emojified);
    } else if (
      message.channel &&
      [
        'email',
        'widgetchat',
        'sms',
        'adachat',
        'digitalgeniuschat',
        'kindlychat',
        'trustpilotreviews',
      ].includes(message.channel)
    ) {
      preparedMarkup = markdownToHTML(
        EmojiOne.shortnameToUnicode(preparedMarkup),
      );
    } else if (
      message.channel &&
      ['facebookmessenger', 'whatsapp', 'twitter'].includes(message.channel)
    ) {
      preparedMarkup = markdownToHTML(preparedMarkup);
    } else if (
      ['conversation:wrapup_message', 'conversation:wrapup_added'].includes(
        message.type,
      )
    ) {
      preparedMarkup = markdownToHTML(
        EmojiOne.shortnameToUnicode(preparedMarkup),
      );
    }
  }

  const html = addTargetAndRelToLinks(preparedMarkup);
  return typeof html === 'string' ? html : '';
}
